import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./OffersPage.css";
import { useNavigate } from "react-router-dom";

export default function OffersPage() {
  const [tokens, setTokens] = useState(0);
  const [welcomeClaimed, setWelcomeClaimed] = useState(false);
  const [weeklyClaimed, setWeeklyClaimed] = useState(false);
  const [weeklyDaysLeft, setWeeklyDaysLeft] = useState(0);
  const [redeemCode, setRedeemCode] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const auth = getAuth();
  const navigate = useNavigate();

  // API from .env
  const API_REACT = process.env.REACT_APP_API_REACT;

  const showMessage = (msg, duration = 4000) => {
    setMessage(msg);
    setTimeout(() => setMessage(""), duration);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) await fetchUserStatus(user);
    });
    return () => unsubscribe();
  }, []);

const fetchUserStatus = async (user) => {
  try {
    const idToken = await user.getIdToken();
    const res = await fetch(`${API_REACT}/api/user-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });

    const data = await res.json();

    if (res.ok) {
      setTokens(data.tokens || 0);
      setWelcomeClaimed(data.welcomeClaimed || false);

      // ----------------- WEEKLY BONUS FIXED LOGIC -----------------
      if (data.lastWeeklyClaim) {
        let lastClaim = null;

        // Check: Firestore Timestamp format
        if (data.lastWeeklyClaim?._seconds) {
          lastClaim = data.lastWeeklyClaim._seconds * 1000;
        } 
        
        // Check: String format (like "23 November 2025 at ...")
        else if (typeof data.lastWeeklyClaim === "string") {
          lastClaim = new Date(Date.parse(data.lastWeeklyClaim)).getTime();
        }

        if (lastClaim) {
          const now = Date.now();
          const diffDays = Math.floor((now - lastClaim) / (1000 * 60 * 60 * 24));

          if (diffDays >= 7) {
            setWeeklyClaimed(false);
            setWeeklyDaysLeft(0);
          } else {
            setWeeklyClaimed(true);
            setWeeklyDaysLeft(7 - diffDays);
          }
        } else {
          // In case parsing fails
          setWeeklyClaimed(false);
          setWeeklyDaysLeft(0);
        }
      } else {
        setWeeklyClaimed(false);
        setWeeklyDaysLeft(0);
      }

      // --------------------------------------------------------

    } else {
      showMessage(data.error || "Failed to fetch status");
    }

  } catch (err) {
    console.error(err);
    showMessage("❌ Failed to fetch user status");
  }
};


  const claimWelcome = async () => {
    if (!currentUser || welcomeClaimed) return;
    setLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_REACT}/api/claim-welcome`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setWelcomeClaimed(true);
        setTokens((prev) => prev + (data.tokensAdded || 0));
        showMessage("🎉 You received 5 tokens!");
      } else {
        showMessage(data.message || data.error || "❌ Claim failed");
      }
    } catch (err) {
      console.error(err);
      showMessage("❌ Claim failed");
    } finally {
      setLoading(false);
    }
  };

  const claimWeekly = async () => {
    if (!currentUser || weeklyClaimed) return;
    setLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_REACT}/api/claim-weekly`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setWeeklyClaimed(true);
        setWeeklyDaysLeft(7);
        setTokens((prev) => prev + (data.tokensAdded || 0));
        showMessage("🎉 You received 1 weekly token!");
      } else {
        showMessage(data.message || data.error || "⏳ Already claimed!");
      }
    } catch (err) {
      console.error(err);
      showMessage("❌ Claim failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return showMessage("Enter a valid code.");
    if (!currentUser) return showMessage("❌ User not authenticated.");
    setLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_REACT}/api/redeem-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken, code: redeemCode.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTokens((prev) => prev + (data.tokensAdded || 0));
        showMessage(
          `✅ Redeemed! Tokens: ${data.tokensAdded || 0}${
            data.plan ? `, Plan: ${data.plan}` : ""
          }`
        );
        setRedeemCode("");
      } else {
        showMessage(data.error || "❌ Invalid code.");
      }
    } catch (err) {
      console.error(err);
      showMessage("❌ Redeem failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="offers-page">
      <button className="backy" onClick={() => navigate("/homepage/profile")}>
        <span className="material-icons">arrow_back</span> Back
      </button>

      <h2>🎉 Welcome to Offers</h2>

      <div className="tokens-display">
        <strong>💰 Your Tokens:</strong> {tokens}
      </div>

      <div className="redeem-bar">
        <input
          type="text"
          placeholder="Enter your code..."
          value={redeemCode}
          onChange={(e) => setRedeemCode(e.target.value)}
          disabled={loading}
        />
        <button onClick={handleRedeem} disabled={loading || !redeemCode.trim()}>
          Redeem
        </button>
      </div>

      {message && <div className="redeem-message">{message}</div>}

      <div className="offer-card big-card welcome-card">
        <h3>🎁 Welcome Bonus</h3>
        <p>
          As a new member, you can claim <strong>5 free tokens</strong> to get
          started. This bonus can only be claimed <strong>once</strong>.
        </p>
        <button onClick={claimWelcome} disabled={welcomeClaimed || loading}>
          {welcomeClaimed ? "Claimed" : "Claim 5 Tokens"}
        </button>
      </div>

      <div className="offer-card big-card weekly-card">
        <h3>📅 Weekly Bonus</h3>
        <p>
          Come back every week and claim <strong>1 free token</strong>. You can
          claim this reward <strong>once every 7 days</strong>.
        </p>
        <button onClick={claimWeekly} disabled={weeklyClaimed || loading}>
          {weeklyClaimed
            ? `Come back in ${weeklyDaysLeft} day(s)`
            : "Claim 1 Token"}
        </button>
      </div>
    </div>
  );
}
